/* eslint-disable max-lines-per-function */
import ParameterValidator from '@mvps-genesis/parameter-validator';

/*
 *--------------------------------------------------------------------------
 * Tabcordion
 *--------------------------------------------------------------------------
 *
 * Provides a responsive and accessible solution for a tab / accordion
 * hybrid implementation.
 *
 */
export default class Tabcordion {

	/**
	 * Parameter Validator class object
	 *
	 * @var	{ParameterValidator} @mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * Tabs container element
	 *
	 * @var {HTMLElement}
	 */
	containerElement;

	/**
	 * Object containing tabcordion element selectors
	 *
	 * @var {Object}
	 */
	selectors;

	/**
	 * List of objects containing tabcordion elements.
	 *
	 * @var Array
	 *
	 * Objects have the following properties:
	 * @prop {HTMLElement} accordionControl - The accordion control element (used in accordion mode)
	 * @prop {HTMLElement} panel - The tab panel element (contains the content)
	 * @prop {HTMLElement} tabControl - The tab control element (used in non-accordion mode)
	 */
	tabcordionElements;

	/**
	 * The tablist element
	 *
	 * @var {HTMLElement}
	 */
	tabListElement;

	/**
	 * Accordion mode setting
	 *
	 * @var {String|Int}
	 */
	accordionMode;

	/**
	 * Flag to determine if accordion mode is active or inactive
	 *
	 * @var {Boolean}
	 */
	isAccordion;

	/**
	 * Accordion mode active class name
	 *
	 * @var {String}
	 */
	accordionActiveClass;

	/**
	 * Active tab class name
	 *
	 * @var {String}
	 */
	tabActiveClass;

	/**
	 * Configuration object for URL hash features
	 *
	 * @var {Object}
	 */
	hashOptions;

	/**
	 * Create a new Tabcordion object.
	 *
	 * @param  {HTMLElement|String}  containerElement                    The tabs container element.
	 * @param  {String}              options.accordionActiveClass        Accordion mode class name. Defaults to 'is-accordion'.
	 * @param  {String}              options.accordionControlSelector    The selector for the accordion control elements. Defaults to '.g-tabcordion__accordion-control'.
	 * @param  {String|Int}          options.accordionMode               Accordion mode setting that controls when the tabs display like an accordion. This can be set to 'auto' to automatically detect and switch to the accordion view when the width of the tabs exceeds the container width. An integer value can also be provided to explicitly set the width to switch to the accordion view. Defaults to 'auto'.
	 * @param  {Object}              options.hashOptions                 Configuration object for URL hash features. If overriding the default object, you MUST provide all properties listed below. See following properties below for default values.
	 * @param  {Boolean}             options.hashOptions.enabled	     Flag to determine if URL hash features are enabled or disabled. Enabling this option will override the tabActiveIndex setting for the initial tab panel activation if a URL location hash is present and matches the id attribute of a tab panel element. Defaults to true.
	 * @param  {Object}              options.hashOptions.scrollIntoView  Scroll into view options object. See here for more information on available options - https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView. Defaults to {behavior: 'smooth', block: 'center'}.
	 * @param  {Boolean}             options.hashOptions.updateLocation  Flag to determine if window location state is updated with hash value corresponding to the active tab panel. Defaults to true.
	 * @param  {String}              options.tabActiveClass              Active tab class name. Defaults to 'is-active'.
	 * @param  {Int}                 options.tabActiveIndex              Set the index of the initial active panel. Defaults to 0.
	 * @param  {String}              options.tabControlSelector          The selector for the tab control elements. Defaults to '.g-tabcordion__tab'.
	 * @param  {String}              options.tabListSelector             The selector for the tab list element. Defaults to '.g-tabcordion__tabs'.
	 * @param  {String}              options.tabPanelContentSelector     The selector for the tab panel content elements. Defaults to '.g-tabcordion__panel-content'.
	 * @param  {String}              options.tabPanelSelector            The selector for the tab panel elements. Defaults to '.g-tabcordion__panel'.
	 */
	constructor (
		containerElement,
		{
			accordionActiveClass = 'is-accordion',
			accordionControlSelector = '.g-tabcordion__accordion-control',
			accordionMode = 'auto',
			hashOptions = {
				enabled: true,
				scrollIntoView: {
					behavior: 'smooth',
					block: 'center'
				},
				updateLocation: true
			},
			tabActiveClass = 'is-active',
			tabActiveIndex = 0,
			tabControlSelector = '.g-tabcordion__tab',
			tabListSelector = '.g-tabcordion__tabs',
			tabPanelContentSelector = '.g-tabcordion__panel-content',
			tabPanelSelector = '.g-tabcordion__panel'
		} = {}
	) {
		this.#validator = new ParameterValidator(this);

		this.containerElement = this.#validator.isElement('containerElement', containerElement);

		this.selectors = {
			accordionControl: this.#validator.isString('options.accordionControlSelector', accordionControlSelector),
			tabControl: this.#validator.isString('options.tabControlSelector', tabControlSelector),
			tabList: this.#validator.isString('options.tabListSelector', tabListSelector),
			tabPanel: this.#validator.isString('options.tabPanelSelector', tabPanelSelector),
			tabPanelContent: this.#validator.isString('options.tabPanelContentSelector', tabPanelContentSelector)
		};

		this.tabListElement = this.#validator.isElement('options.tabListElement', this.selectors.tabList, {container: this.containerElement});
		this.tabActiveClass = this.#validator.isString('options.tabActiveClass', tabActiveClass);
		this.#validator.isInt('options.tabActiveIndex', tabActiveIndex);

		this.accordionActiveClass = this.#validator.isString('options.accordionActiveClass', accordionActiveClass);
		this.accordionMode = this.#validator.isDefined('options.accordionMode', accordionMode);

		if (this.accordionMode !== 'auto') {
			this.accordionMode = this.#validator.isInt('options.accordionMode must be "auto" or', parseInt(this.accordionMode, 10));
		}

		this.hashOptions = this.#validator.isObject('options.hashOptions', hashOptions);
		this.#validator.isBoolean('options.hashOptions.enabled', this.hashOptions.enabled);
		this.#validator.isObject('options.hashOptions.scrollIntoView', this.hashOptions.scrollIntoView);
		this.#validator.isBoolean('options.hashOptions.updateLocation', this.hashOptions.updateLocation);

		this.init(tabActiveIndex);
	}

	/**
	 * Initialize default properties and configurations.
	 *
	 * @param  {Int} tabActiveIndex The index of the active tab.
	 */
	init (tabActiveIndex = 0) {
		this.setTabcordionElements();
		this.setAccordionModeDetection();
		this.setActivePanel(tabActiveIndex);

		if (this.hashOptions.enabled) {
			window.addEventListener('hashchange', this.onHashChange.bind(this));
		}
	}

	/**
	 * Set the list of tabcordion element objects.
	 *
	 * @throws {TypeError} Will throw an error if an invalid selector is provided for the tabpanel, tab control, or accordion control elements, as well as if no tabcordion elements are set.
	 */
	setTabcordionElements () {
		this.tabcordionElements = [];

		const tabPanelElements = this.containerElement.querySelectorAll(this.selectors.tabPanel);
		const tabPanelElementsLen = tabPanelElements.length;

		if (!tabPanelElementsLen) {
			throw new TypeError(`${this.constructor.name}::setTabcordionElements Invalid tab panel elements selector`);
		}

		for (let i = 0; i < tabPanelElementsLen; i++) {
			const tabPanelElement = tabPanelElements[i];
			const tabControlElement = this.containerElement.querySelector(`${this.selectors.tabControl}[aria-controls="${tabPanelElement.id}"]`);

			if (!(tabControlElement instanceof HTMLElement)) {
				throw new TypeError(`${this.constructor.name}::setTabcordionElements Invalid tab control element selector for '#${tabPanelElement.id}' tab panel element`);
			}

			const accordionControlElement = this.containerElement.querySelector(`${this.selectors.accordionControl}[aria-controls="${tabPanelElement.id}"]`);

			if (!(accordionControlElement instanceof HTMLElement)) {
				throw new TypeError(`${this.constructor.name}::setTabcordionElements Invalid accordion control element selector for '#${tabPanelElement.id}' tab panel element`);
			}

			tabControlElement.index = i;
			accordionControlElement.index = i;

			const tabcordionElement = {
				accordionControl: accordionControlElement,
				panel: tabPanelElement,
				tabControl: tabControlElement
			};

			this.tabcordionElements.push(tabcordionElement);
			this.bindTabcordionElementEvents(tabcordionElement);
		}

		if (!this.tabcordionElements.length) {
			throw new TypeError(`${this.constructor.name}::setTabcordionElements One or more tabcordion elements must exist`);
		}

		this.tabcordionElements[0].tabControl.setAttribute('tabindex', '0');
	}

	/**
	 * Active tabpanel element handling.
	 *
	 * @param {Int} index The index of the active tabcordion element.
	 */
	setActivePanel (index) {
		let activeTabcordionElement = this.tabcordionElements[index];

		if (!this.isTabcordionElement(activeTabcordionElement)) {
			activeTabcordionElement = this.tabcordionElements[0];
		}

		let activeTabOptions = {
			disableLocationUpdate: true,
			focus: false
		};

		if (this.hashOptions.enabled && window.location.hash) {
			const hashElements = this.getElementsByHash(window.location.hash);

			if (hashElements) {
				activeTabOptions.scrollIntoView = this.hashOptions.scrollIntoView;

				if (hashElements !== activeTabcordionElement) {
					activeTabcordionElement = hashElements;
					activeTabOptions.focus = true;
				}

				if (this.isAccordion) {
					this.hidePanel(this.getActiveElement());
				}

				this.showPanel(activeTabcordionElement, activeTabOptions);
				return;
			}
		}

		this.showPanel(activeTabcordionElement, activeTabOptions);
	}

	/**
	 * Set the accordion mode detection.
	 */
	setAccordionModeDetection () {
		if (this.accordionMode === 'auto') {
			const accordionModeObserver = new ResizeObserver((entries) => {
				for (const entry of entries) {
					this.observeAccordionMode(entry.contentRect.width);
				}
			});

			accordionModeObserver.observe(this.tabListElement);
		} else {
			const mediaQueryList = window.matchMedia(`(max-width: ${this.accordionMode / 16}em)`);

			this.watchAccordionMode(mediaQueryList);

			mediaQueryList.addEventListener('change', (event) => {
				this.watchAccordionMode(event.target);
			});
		}
	}

	/**
	 * Set observers for accordion mode detection.
	 *
	 * @param  {Number} tabListWidth The width of the tablist element.
	 */
	observeAccordionMode (tabListWidth) {
		let totalTabWidth = 0;

		this.tabcordionElements.forEach((element) => {
			totalTabWidth += element.tabControl.offsetWidth;
		});

		this.setAccordionMode(totalTabWidth >= tabListWidth);
	}

	/**
	 * Observe screen width changes for accordion mode.
	 *
	 * @param  {MediaQueryList} mediaQueryList The MediaQueryList object.
	 */
	watchAccordionMode (mediaQueryList) {
		this.setAccordionMode(mediaQueryList.matches);
	}

	/**
	 * Accordion mode functionality handling.
	 *
	 * @param {Boolean} enableAccordionMode Flag to determine if accordion mode is enabled or disabled.
	 */
	setAccordionMode (enableAccordionMode) {
		if (enableAccordionMode) {
			this.isAccordion = true;
			this.containerElement.classList.add(this.accordionActiveClass);
		} else {
			this.isAccordion = false;
			this.containerElement.classList.remove(this.accordionActiveClass);
		}

		let activeTabcordionElements = [];

		// Initialize panel elements
		this.tabcordionElements.forEach((element) => {
			if (element.tabControl.classList.contains(this.tabActiveClass)) {
				element.panel.removeAttribute('hidden');
				activeTabcordionElements.push(element);
			} else {
				element.panel.setAttribute('hidden', '');
			}

			element.panel.setAttribute('tabindex', '0');
		});

		// Hide additional panels when switching from accordion to tab view
		if (!this.isAccordion && activeTabcordionElements.length > 1) {
			let activeTabcordionElement = activeTabcordionElements[0];

			if (this.hashOptions.enabled && window.location.hash) {
				const hashElements = this.getElementsByHash(window.location.hash);

				if (hashElements) {
					activeTabcordionElement = hashElements;
				}
			}

			activeTabcordionElements.forEach((element) => {
				if (element !== activeTabcordionElement) {
					this.hidePanel(element);
				}
			});
		}
	}

	/**
	 * Bind the tabcordion element events.
	 *
	 * @param  {Object} tabcordionElement The tabcordion element object.
	 */
	bindTabcordionElementEvents (tabcordionElement) {
		tabcordionElement.tabControl.addEventListener('click', this.onTabcordionClick.bind(this, tabcordionElement));
		tabcordionElement.tabControl.addEventListener('keydown', this.onTabKeydown.bind(this, tabcordionElement));

		tabcordionElement.accordionControl.addEventListener('click', this.onTabcordionClick.bind(this, tabcordionElement));
		tabcordionElement.accordionControl.addEventListener('keydown', this.onAccordionKeydown.bind(this, tabcordionElement));

		tabcordionElement.panel.addEventListener('keydown', this.onPanelKeydown.bind(this, tabcordionElement));
	}

	/**
	 * Handle tabcordion element click events
	 *
	 * @param  {Object} tabcordionElement The tabcordion element object.
	 * @param  {Event}  event             The click event.
	 */
	onTabcordionClick (tabcordionElement, event) {
		event.preventDefault();

		if (this.isAccordion && tabcordionElement.accordionControl.classList.contains(this.tabActiveClass)) {
			this.hidePanel(tabcordionElement);
		} else {
			this.showPanel(tabcordionElement);
		}
	}

	/**
	 * Create a tabcordion element click event
	 */
	createTabcordionClickEvent () {
		if (!this.tabcordionClickEvent) {
			this.tabcordionClickEvent = new MouseEvent('click');
		}

		return this.tabcordionClickEvent;
	}

	/**
	 * Handle tab element keydown events.
	 *
	 * @param  {Object} tabcordionElement The tabcordion element object.
	 * @param  {Event}  event             The keydown event.
	 */
	onTabKeydown (tabcordionElement, event) {
		const key = event.key.toLowerCase();

		switch (key) {
			case 'arrowleft':
			case 'arrowright':
				event.preventDefault();

				this.handleTabArrowKey(tabcordionElement.tabControl, key);
				break;

			case 'end':
				event.preventDefault();

				this.focusLastTab();
				break;

			case 'home':
				event.preventDefault();

				this.focusFirstTab();
				break;

			case ' ':
			case 'spacebar':
				event.preventDefault();

				tabcordionElement.tabControl.dispatchEvent(this.createTabcordionClickEvent());
				break;

			default:
				break;
		}
	}

	/**
	 * Handle accordion element keydown events.
	 *
	 * @param  {Object} tabcordionElement The tabcordion element object.
	 * @param  {Event}  event             The keydown event.
	 */
	onAccordionKeydown (tabcordionElement, event) {
		const key = event.key.toLowerCase();

		switch (key) {
			case 'arrowup':
			case 'arrowdown':
				event.preventDefault();

				this.handleAccordionArrowKey(tabcordionElement.accordionControl, key);
				break;

			case 'end':
				event.preventDefault();

				this.focusLastAccordion();
				break;

			case 'home':
				event.preventDefault();

				this.focusFirstAccordion();
				break;

			case ' ':
			case 'spacebar':
				event.preventDefault();

				tabcordionElement.accordionControl.dispatchEvent(this.createTabcordionClickEvent());
				break;

			default:
				break;
		}
	}

	/**
	 * Handle tabpanel element keydown events.
	 *
	 * @param  {Object} tabcordionElement The tabcordion element object.
	 * @param  {Event}  event             The keydown event.
	 */
	onPanelKeydown (tabcordionElement, event) {
		const key = event.key.toLowerCase();

		switch (key) {
			case 'tab':
				if (event.shiftKey && !this.isAccordion) {
					event.preventDefault();

					tabcordionElement.tabControl.focus();
				}

				break;

			default:
				break;
		}
	}

	/**
	 * Handle window hashchange events
	 *
	 * @param  {Event} event The hashchange event.
	 */
	onHashChange (event) {
		const hashValue = event.target.location.hash;

		if (!hashValue) {
			return;
		}

		const tabcordionElement = this.getElementsByHash(hashValue);

		if (!tabcordionElement) {
			return;
		}

		event.preventDefault();

		this.activateTab(tabcordionElement.tabControl, {
			scrollIntoView: this.hashOptions.scrollIntoView
		});
	}

	/**
	 * Handle tab element arrowkey events.
	 *
	 * @param  {HTMLElement} tabElement The tab element.
	 * @param  {DOMString}   key        The keyboard event's key property.
	 */
	handleTabArrowKey (tabElement, key) {
		if (typeof tabElement.index === 'undefined') {
			return;
		}

		const indexDirection = key === 'arrowleft' ? -1 : 1;
		const targetIndex = parseInt(tabElement.index + indexDirection, 10);

		if (isNaN(targetIndex)) {
			return;
		}

		if (this.tabcordionElements.hasOwnProperty(targetIndex)) {
			this.tabcordionElements[targetIndex].tabControl.focus();
		} else if (indexDirection === -1) {
			this.focusLastTab();
		} else if (indexDirection === 1) {
			this.focusFirstTab();
		}
	}

	/**
	 * Handle accordion element arrowkey events.
	 *
	 * @param  {HTMLElement} accordionElement The accordion element.
	 * @param  {DOMString}   key              The keyboard event's key property.
	 */
	handleAccordionArrowKey (accordionElement, key) {
		if (typeof accordionElement.index === 'undefined') {
			return;
		}

		const indexDirection = key === 'arrowup' ? -1 : 1;
		const targetIndex = parseInt(accordionElement.index + indexDirection, 10);

		if (isNaN(targetIndex)) {
			return;
		}

		if (this.tabcordionElements.hasOwnProperty(targetIndex)) {
			this.tabcordionElements[targetIndex].accordionControl.focus();
		} else if (indexDirection === -1) {
			this.focusLastAccordion();
		} else if (indexDirection === 1) {
			this.focusFirstAccordion();
		}
	}

	/**
	 * Test if element is a tabcordion element.
	 *
	 * @param  {Object}  element The object to test.
	 * @return {Boolean}
	 */
	isTabcordionElement (element) {
		return [
			'accordionControl',
			'panel',
			'tabControl'
		].every((property) => {
			return element.hasOwnProperty(property) && element[property] instanceof HTMLElement;
		});
	}

	/**
	 * Hide a tabpanel element.
	 *
	 * @param  {HTMLElement} panelElement The tabpanel element.
	 * @throws {TypeError}                Will throw an error if the provided tabpanel element is not found in the tabcordion elements list.
	 */
	hidePanel (panelElement) {
		const tabcordionElement = this.isTabcordionElement(panelElement) ? panelElement : this.getElementsByPanelElement(panelElement);

		if (!tabcordionElement) {
			throw new TypeError(`${this.constructor.name}::hidePanel Invalid 'panelElement' provided`);
		}

		tabcordionElement.tabControl.classList.remove(this.tabActiveClass);
		tabcordionElement.tabControl.setAttribute('aria-expanded', 'false');

		tabcordionElement.accordionControl.classList.remove(this.tabActiveClass);
		tabcordionElement.accordionControl.setAttribute('aria-expanded', 'false');

		tabcordionElement.panel.setAttribute('hidden', '');
	}

	/**
	 * Show a tabpanel element.
	 *
	 * @param  {HTMLElement}    panelElement                  The tabpanel element.
	 * @param  {Boolean}        options.disableLocationUpdate Disable the functionality for the window location updates.
	 * @param  {Boolean}        options.focus                 Flag to determine if focus should be set on the tabpanel element.
	 * @param  {Object|null}    options.scrollIntoView        Scroll into view options for tabpanel element scroll to functionality. Providing a null value disables the scroll to functionality.
	 * @return {Undefined|void}                               Returns an undefined value if the provided tabpanel element is already the active panel.
	 * @throws {TypeError}                                    Will throw an error if the provided tabpanel element is not found in the tabcordion elements list.
	 */
	showPanel (panelElement, {disableLocationUpdate = false, focus = true, scrollIntoView = null} = {}) {
		const tabcordionElement = this.isTabcordionElement(panelElement) ? panelElement : this.getElementsByPanelElement(panelElement);

		if (!tabcordionElement) {
			throw new TypeError(`${this.constructor.name}::showPanel Invalid 'panelElement' provided`);
		}

		if (tabcordionElement.tabControl.classList.contains(this.tabActiveClass)) {
			if (scrollIntoView !== null) {
				this.scrollToPanel(tabcordionElement.panel, scrollIntoView);
			}

			if (focus) {
				tabcordionElement.panel.focus();
			}

			return;
		}

		if (!this.isAccordion) {
			this.hidePanel(this.getActiveElement());
		}

		tabcordionElement.tabControl.classList.add(this.tabActiveClass);
		tabcordionElement.tabControl.setAttribute('aria-expanded', 'true');

		tabcordionElement.accordionControl.classList.add(this.tabActiveClass);
		tabcordionElement.accordionControl.setAttribute('aria-expanded', 'true');

		tabcordionElement.panel.removeAttribute('hidden');

		if (scrollIntoView !== null) {
			this.scrollToPanel(tabcordionElement.panel, scrollIntoView);
		}

		if (focus) {
			tabcordionElement.panel.focus();
		}

		if (!disableLocationUpdate && this.hashOptions.enabled && this.hashOptions.updateLocation) {
			this.updateLocationHash(tabcordionElement.panel);
		}
	}

	/**
	 * Retrieve the active tabcordion element object.
	 *
	 * @return {Object|Undefined}
	 */
	getActiveElement () {
		return this.tabcordionElements.find((element) => {
			return element.tabControl.classList.contains(this.tabActiveClass);
		});
	}

	/**
	 * Retrieve a tabcordion element object by control element.
	 *
	 * @param  {HTMLElement}      controlElement The control element.
	 * @return {Object|Undefined}
	 */
	getElementsByControlElement (controlElement) {
		return this.tabcordionElements.find((element) => {
			return element.tabControl === controlElement || element.accordionControl === controlElement;
		});
	}

	/**
	 * Retrieve a tabcordion element object by tabpanel element.
	 *
	 * @param  {HTMLElement}      panelElement The tabpanel element.
	 * @return {Object|Undefined}
	 */
	getElementsByPanelElement (panelElement) {
		return this.tabcordionElements.find((element) => {
			return element.panel === panelElement;
		});
	}

	/**
	 * Retrieve a tabcordion element object by hash value.
	 *
	 * @param  {String}           hashVal The hash value.
	 * @return {Object|Undefined}
	 */
	getElementsByHash (hashVal) {
		return this.tabcordionElements.find((element) => {
			return `#${element.panel.id}` === hashVal;
		});
	}

	/**
	 * Scroll to tabpanel element functionality
	 *
	 * @param  {HTMLElement} panelElement   The tabpanel element.
	 * @param  {Object}      scrollIntoView Scroll into view options.
	 */
	scrollToPanel (panelElement, scrollIntoView) {
		if (typeof panelElement.contentAnimationDuration !== 'number') {
			panelElement.contentAnimationDuration = 0;

			const panelContentElement = panelElement.querySelector(this.selectors.tabPanelContent);

			if (panelContentElement) {
				panelElement.contentComputedStyle = window.getComputedStyle(panelContentElement, null);
				panelElement.contentAnimationDuration = parseFloat(panelElement.contentComputedStyle.getPropertyValue('animation-duration')) * 1000;
			}
		}

		setTimeout(() => {
			if ('scrollBehavior' in document.documentElement.style) {
				panelElement.scrollIntoView(scrollIntoView);
			} else if (typeof scrollIntoView === 'object') {
				panelElement.scroll({
					behavior: scrollIntoView.behavior ?? 'auto',
					left: 0,
					top: panelElement.getBoundingClientRect().top / 2
				});
			}
		}, panelElement.contentAnimationDuration);
	}

	/**
	 * Update window location hash functionality.
	 *
	 * @param  {HTMLElement} panelElement The tabpanel element.
	 */
	updateLocationHash (panelElement) {
		const panelElementHash = `#${panelElement.id}`;

		if (panelElementHash !== window.location.hash) {
			window.history.replaceState(null, '', window.location.pathname + window.location.search + panelElementHash);
		}
	}

	/**
	 * Set focus on the first tab control element.
	 */
	focusFirstTab () {
		this.focusFirstControlElement('tab');
	}

	/**
	 * Set focus on the last tab control element.
	 */
	focusLastTab () {
		this.focusLastControlElement('tab');
	}

	/**
	 * Set focus on the first accordion control element.
	 */
	focusFirstAccordion () {
		this.focusFirstControlElement('accordion');
	}

	/**
	 * Set focus on the last accordion control element.
	 */
	focusLastAccordion () {
		this.focusLastControlElement('accordion');
	}

	/**
	 * Set focus on the first control element.
	 *
	 * @param  {String} controlName The name of the control element.
	 */
	focusFirstControlElement (controlName) {
		this.tabcordionElements[0][`${controlName}Control`].focus();
	}

	/**
	 * Set focus on the last control element.
	 *
	 * @param  {String} controlName The name of the control element.
	 */
	focusLastControlElement (controlName) {
		this.tabcordionElements[this.tabcordionElements.length - 1][`${controlName}Control`].focus();
	}
}
